import type { TxSummary } from '@apm/contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCoalesced } from '../hooks/useCoalesced';
import { currentQuery, mergeQuery, navigate, replaceQuery, useCurrentQuery } from '../router';
import { useServiceFilter } from '../services/service-filter';
import { streamStore } from '../store/stream';
import { useLiveSelector } from '../store/useStream';
import { MSG, TIP, TX_TYPE_LABEL } from '../terms';
import { formatCount, formatDuration, formatFull, toLocalInput } from '../time';
import {
  Button,
  EmptyState,
  ErrorText,
  Mark,
  type Range,
  RangePicker,
  rangeMs,
  rangeOf,
  Section,
  Toggle,
  Tooltip,
} from '../ui';
import { countMatching, type RowFilter } from './filter';
import { PAST_LIMIT, PAST_SLICES } from './past';
import { ERROR_COLOR, FAIL_COLOR, TYPE_COLOR, TYPE_SHAPE } from './renderer';
import { SelectionTable } from './SelectionTable';
import type { RectSelection } from './scale';
import { usePastRows } from './usePastRows';
import { useXViewStats, XView } from './XView';

/**
 * 끝난 요청 분포도 (요청 화면의 위쪽 구획).
 *
 * 7회차부터 점을 누르면 그 자리에 요약을 펼치지 않고 `#/tx/{요청 번호}` 상세로 간다(결정 3).
 * 상세가 요약 카드의 내용을 전부 담고 DB 조회 목록까지 보이므로 미리보기 카드는 뺐다.
 *
 * 19회차에 실시간 연결 기본 제외(결정 4)·오류만 보기(C-3)·자료 없음 안내(C-7)가 붙었다.
 * 22-B회차에 탭에서 내려와 검색과 한 화면(`requests/RequestsPage`)이 됐다.
 *
 * 23-A회차에 **과거 보기**가 붙었다(조정자 제안 8, 후보 C-1). 그전까지 이 그림은 최근 5분만
 * 볼 수 있어 "어제 그 시간에 뭐가 느렸나"를 그림으로는 볼 수 없었다. 과거 보기로 바꾸면
 * 그 기간의 끝난 요청을 검색 API로 받아 같은 캔버스에 그린다. 점 누르기·끌어 고르기·CSV는
 * 그대로다. 서버는 손대지 않았다 — 검색 API의 `from`·`to`·`limit`이면 된다.
 *
 * 상한을 넘는 기간은 **시간대별로 고르게 뽑는다**(23-A회차 리뷰 2, `past.ts`). 최신 5,000건만
 * 받으면 앞 시간대가 통째로 비어 보인다.
 *
 * 과거 여부·기간·거르기는 **주소에 싣는다**(리뷰 3). 새로고침하거나 남에게 보내도 같은 화면이
 * 열리고, 점을 눌러 상세로 갔다 돌아와도 보던 자리로 온다. 키는 `x`로 시작해 아래 검색 구획의
 * 조건과 섞이지 않고, 서로 상대의 키를 지우지 않도록 둘 다 `mergeQuery`를 거친다.
 */

/**
 * 분포도가 주소에 싣는 키 전부 (44회차). 아래 검색 구획은 이 키가 바뀐 것을 제 조건이
 * 바뀐 것으로 보면 안 된다 — 거르기만 켰는데 검색이 다시 돌고 고치던 조건이 덮였다
 */
export const XVIEW_QUERY_KEYS = ['xmode', 'xfrom', 'xto', 'xsse', 'xerr'] as const;

export function XViewPanel({ query }: { query?: string } = {}) {
  const service = useServiceFilter();
  const initial = useMemo(() => parsePastQuery(query), []);
  const [selection, setSelection] = useState<RectSelection>();
  const [stats, reportStats] = useXViewStats();
  const [filter, setFilter] = useState<RowFilter>(initial.filter);
  /** 과거 보기 기간. undefined면 실시간(최근 5분)이다 */
  const [past, setPast] = useState<Range | undefined>(initial.past);
  // 판 번호와 인스턴스 목록만 본다 (D-C1)
  const txVersion = useLiveSelector((live) => live.txVersion);
  const liveInstances = useLiveSelector((live) => live.instances);
  // 5만 행 훑기는 1초에 한 번으로 묶는다 (D-C5)
  const countVersion = useCoalesced(txVersion, 1_000);

  const bounds = useMemo(() => (past === undefined ? undefined : rangeMs(past)), [past]);
  const loaded = usePastRows(bounds, service === '' ? undefined : service);
  const displayFilter = useMemo<RowFilter>(
    () => ({ ...filter, service: service === '' ? undefined : service }),
    [filter, service],
  );

  // 끌어 고른 표(`memo`)에 넘기므로 판마다 같은 함수여야 한다 (D-C3). 주소는 누른 순간에 읽는다
  const open = useCallback((row: TxSummary | undefined) => {
    if (row) {
      navigate({
        name: 'detail',
        traceId: row.traceId,
        from: 'requests',
        service: row.service,
        // 돌아왔을 때 보던 기간·거르기가 그대로여야 한다
        back: currentQuery(),
      });
    }
  }, []);
  const clearSelection = useCallback(() => setSelection(undefined), []);
  // 표 링크의 복귀 조건. 조건이 바뀔 때만 표가 다시 그려진다 (R62-2)
  const back = useCurrentQuery();

  // 조건을 주소에 남긴다. 아래 검색 구획의 키는 건드리지 않는다 (리뷰 3)
  useEffect(() => {
    replaceQuery({
      name: 'requests',
      query: mergeQuery(currentQuery(), {
        xmode: past === undefined ? undefined : 'past',
        xfrom: bounds?.from,
        xto: bounds?.to,
        xsse: filter.includeSse ? '1' : undefined,
        xerr: filter.errorsOnly ? '1' : undefined,
      }),
    });
  }, [past, bounds?.from, bounds?.to, filter]);

  // 거르기·기간을 바꾸면 앞의 선택은 뜻이 달라진다. 헷갈리지 않게 지운다
  useEffect(() => setSelection(undefined), [filter, past, service]);

  // Esc로 드래그 선택을 푼다
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelection(undefined);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // 걸러 남는 건수. 점이 0개일 때 "왜 비었는지"를 가르는 값이다 (C-7)
  const rows = past === undefined ? undefined : loaded.rows;
  const shown = useMemo(
    () => countMatching(rows ?? streamStore.getRows(), displayFilter),
    [countVersion, displayFilter, rows],
  );
  const visibleInstances = useMemo(
    () => liveInstances.filter((item) => service === '' || item.service === service).length,
    [liveInstances, service],
  );

  // 과거 보기는 가로축을 그 기간에 못 박는다. 오른쪽 끝이 끝 시각이다
  const axis =
    bounds?.from === undefined || bounds.to === undefined
      ? undefined
      : { now: bounds.to, windowMs: Math.max(1, bounds.to - bounds.from) };

  return (
    <>
      <Section
        boxed
        title="끝난 요청 분포도"
        note={[
          past === undefined
            ? '최근 5분. 가로축은 끝난 시각(오른쪽이 지금), 세로축은 응답시간.'
            : '고른 기간. 가로축은 끝난 시각, 세로축은 응답시간.',
          '점을 누르면 상세, 드래그하면 그 범위의 목록.',
          '세로축 최대는 가장 느린 요청에 맞춰 늘어난다',
        ]}
        badge={`${formatCount(shown)}건`}
        actions={
          <div className="flex flex-wrap items-center gap-4">
            <Toggle
              id="xview-include-sse"
              checked={filter.includeSse}
              onChange={(includeSse) => setFilter((prev) => ({ ...prev, includeSse }))}
              label="실시간 연결 포함"
              hint={TIP.sseExcluded}
            />
            <Toggle
              id="xview-errors-only"
              checked={filter.errorsOnly}
              onChange={(errorsOnly) => setFilter((prev) => ({ ...prev, errorsOnly }))}
              label="오류·실패만"
            />
            {past === undefined ? (
              <Button size="sm" onClick={() => setPast(rangeOf(1))}>
                과거 보기
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={loaded.reload} disabled={loaded.loading}>
                  {loaded.loading ? '읽는 중…' : '새로고침'}
                </Button>
                <Button size="sm" variant="primary" onClick={() => setPast(undefined)}>
                  실시간으로
                </Button>
              </>
            )}
          </div>
        }
      >
        {past !== undefined ? (
          <div className="mb-4 flex flex-wrap items-end gap-4">
            <RangePicker idPrefix="xview" value={past} onChange={setPast} />
          </div>
        ) : null}

        {loaded.error !== undefined ? (
          <div className="mb-3">
            <ErrorText>{loaded.error}</ErrorText>
          </div>
        ) : null}

        <div className="relative h-[460px] overflow-hidden rounded-xl border border-hair bg-panel">
          <XView
            onPick={open}
            onSelectRect={setSelection}
            onStats={reportStats}
            filter={displayFilter}
            rows={rows}
            window={axis}
          />
          {shown === 0 ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6">
              <div className="pointer-events-auto max-w-md">
                <EmptyState
                  title={emptyTitle(visibleInstances, filter, past !== undefined, service)}
                  hint={emptyHint(visibleInstances, filter, past !== undefined, service)}
                />
              </div>
            </div>
          ) : null}
        </div>

        {past !== undefined && bounds?.from !== undefined && bounds.to !== undefined ? (
          <p className="tabular mt-3 text-xs text-muted">
            <span className="block">
              {formatFull(bounds.from)} ~ {formatFull(bounds.to)}
            </span>
            {loaded.total > loaded.received ? (
              <span className="block text-fail">
                이 기간에 {formatCount(loaded.total)}건이 있어 {formatCount(loaded.received)}건만
                그렸다
                {loaded.sampled
                  ? ` (기간을 ${PAST_SLICES}조각으로 나눠 고르게 뽑았다)`
                  : ` (조회 상한 ${formatCount(PAST_LIMIT)}건)`}
                . 기간을 좁히면 전부 보인다
              </span>
            ) : null}
          </p>
        ) : null}

        <div className="tabular mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted">
          {(['http', 'sse', 'mcp', 'worker', 'pdf', 'slow'] as const).map((type) => (
            <Legend
              key={type}
              color={TYPE_COLOR[type]}
              shape={TYPE_SHAPE[type]}
              label={TX_TYPE_LABEL[type]}
            />
          ))}
          <Legend color={FAIL_COLOR} shape="circle" label="실패 (4xx·DB 오류)" />
          <Legend color={ERROR_COLOR} shape="circle" label="오류 (5xx·예외)" />
          <span className="ml-auto">
            세로축 최대 {formatDuration(stats.maxMs)} · 화면 {formatCount(stats.points)}점 ·{' '}
            <Tooltip text="한 프레임을 그리는 데 걸린 시간. 16ms를 넘으면 화면이 끊긴다">
              프레임 {stats.frameMs.toFixed(1)}ms
            </Tooltip>
          </span>
        </div>
      </Section>

      {selection ? (
        <SelectionTable selection={selection} back={back} onPick={open} onClear={clearSelection} />
      ) : null}
    </>
  );
}

/**
 * 주소의 조건 → 처음 화면 상태 (23-A회차 리뷰 3).
 * `xmode=past`가 있고 기간이 숫자일 때만 과거 보기로 연다. 모르는 값은 실시간이다
 */
export function parsePastQuery(
  query: string | undefined,
  now: number = Date.now(),
): { past?: Range; filter: RowFilter } {
  const params = new URLSearchParams(query ?? '');
  const filter: RowFilter = {
    includeSse: params.get('xsse') === '1',
    errorsOnly: params.get('xerr') === '1',
  };
  if (params.get('xmode') !== 'past') return { filter };
  // 키가 없으면 `Number(null)`이 0이라 유한수 검사를 지난다. 끝이 1970년이 되지 않게 둘 다 본다 (44회차)
  const rawFrom = params.get('xfrom');
  const rawTo = params.get('xto');
  const from = rawFrom === null || rawFrom === '' ? Number.NaN : Number(rawFrom);
  const to = rawTo === null || rawTo === '' ? Number.NaN : Number(rawTo);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) {
    // 기간이 없거나 모양이 틀리면 최근 1시간으로 연다. 과거 보기라는 뜻은 살린다
    return { past: rangeOf(1, now), filter };
  }
  return { past: { from: toLocalInput(from), to: toLocalInput(to) }, filter };
}

export function emptyTitle(
  instances: number,
  filter: RowFilter,
  past: boolean,
  service = '',
): string {
  const target = service === '' ? '' : `${service}의 `;
  if (past) {
    return filter.errorsOnly
      ? `이 기간에 ${target}오류·실패가 없다`
      : `이 기간에 ${target}끝난 요청이 없다`;
  }
  if (instances === 0)
    return service === '' ? MSG.noAgent : `${service}에 보고 중인 인스턴스가 없다`;
  if (filter.errorsOnly) {
    return service === '' ? '최근 5분에 오류·실패가 없다' : `${target}최근 5분 오류·실패가 없다`;
  }
  return service === '' ? '최근 5분에 완료된 요청이 없다' : `${target}최근 5분 완료된 요청이 없다`;
}

export function emptyHint(
  instances: number,
  filter: RowFilter,
  past: boolean,
  service = '',
): string {
  if (past) {
    if (filter.errorsOnly) return '필터를 끄면 정상으로 끝난 요청까지 본다';
    if (!filter.includeSse) {
      return "실시간 연결만 있을 수 있다. '실시간 연결 포함'을 켜 볼 것";
    }
    return '기간을 넓히거나 아래 검색으로 조건을 좁혀 볼 것';
  }
  if (instances === 0) {
    return service === ''
      ? MSG.noAgentHint
      : `${service}의 에이전트 연결과 APM_ENDPOINT를 확인할 것`;
  }
  if (filter.errorsOnly) return '필터를 끄면 정상으로 끝난 요청까지 본다';
  if (!filter.includeSse) {
    return "실시간 연결만 열려 있을 수 있다. '실시간 연결 포함'을 켜 볼 것";
  }
  return '인스턴스는 보고 중인데 이 5분 동안 완료된 요청이 없다';
}

function Legend({
  color,
  shape,
  label,
}: {
  color: string;
  shape: 'circle' | 'square' | 'triangle' | 'diamond' | 'cross' | 'x';
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Mark color={color} shape={shape} />
      {label}
    </span>
  );
}
